import dayjs from "dayjs";
import { cloneDeep } from "lodash";

import { filterMarketCarsBySearch } from "@/utils/marketCarSearch";

import type { ListTableColumnSortBy } from "@/components/elements";
import type { PaginatedList } from "@/types/entity";
import type { MarketCar, MarketCarColumns, PerformancePayload } from "@/types/market";
import type { Performance, PerformanceChart } from "@/types/performance";
import type { QueryParams } from "./types";

export function mapPayloadToMarketPerfomance(
  data: PerformancePayload,
  perfomanceName = "average",
  hideLastMonth = true
) {
  const currentMonth = hideLastMonth ? dayjs().format("YYYY-MM") : false;
  const benchmarks: PerformanceChart[] = [
    {
      name: "superb",
      data: [],
    },
    {
      name: "excellent",
      data: [],
    },
    {
      name: "good",
      data: [],
    },
    {
      name: "fair",
      data: [],
    },
  ];
  const performance: PerformanceChart = {
    name: perfomanceName,
    data: [],
  };
  data.forEach((item) => {
    // The last month is incomplete, so we skip it
    if (item.month !== currentMonth) {
      performance.data.push({ date: item.month, value: item.average });
      benchmarks[0].data.push({ date: item.month, value: item.superb });
      benchmarks[1].data.push({ date: item.month, value: item.excellent });
      benchmarks[2].data.push({ date: item.month, value: item.good });
      benchmarks[3].data.push({ date: item.month, value: item.fair });
    }
  });

  return {
    benchmarks,
    performance,
  } as Performance;
}

function sortDataBy(id: MarketCarColumns, sortBy: ListTableColumnSortBy = "asc") {
  return (a: MarketCar, b: MarketCar) => {
    const aValue = a[id];
    const bValue = b[id];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortBy === "asc" ? -1 : 1;
    if (bValue == null) return sortBy === "asc" ? 1 : -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortBy === "asc" ? aValue - bValue : bValue - aValue;
    }

    return sortBy === "asc"
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  };
}

export function getDataFilteredAndSorted(response: PaginatedList<MarketCar>, params: QueryParams) {
  let data = cloneDeep(response!);
  const { parameters, page, pageSize, sort } = params;
  let results = [...data.results];

  // Client-side filtering
  if (parameters?.full_text) {
    results = filterMarketCarsBySearch(results, parameters.full_text);
  }

  // Client-side sorting
  if (sort) {
    const sorter = sortDataBy(sort.id, sort.sortBy);
    results = results.sort(sorter);
  }

  // Client-side pagination
  const startIndex = ((page || 1) - 1) * (pageSize || 25);
  const endIndex = startIndex + (pageSize || 25);
  data.total = results.length;
  data.results = results.slice(startIndex, endIndex);
  data.page = page || 1;
  data.page_size = pageSize || 25;

  return data;
}
